import { Inject, Injectable } from '@angular/core';
import { AuthService } from 'app/services/auth.service';
import { Clock } from 'app/services/clock';
import { DeviceRepository } from 'app/services/database/device.repository';
import { DevicesGroupRepository } from 'app/services/database/devicesGroup.repository';
import { PresentationRepository } from 'app/services/database/presentation.repository';
import { StorageCategories, StorageService } from 'app/services/database/storage.service';
import { UserRepository } from 'app/services/database/user.repository';
import { PresentationHandler } from 'app/services/presentation/presentationHandler';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { ScheduleModel } from 'app/views/devices/models/schedule.model';
import { ScheduleEntryModel } from 'app/views/devices/models/scheduleEntry.model';
import { LayoutResolver } from 'app/views/presentations/layouts/layoutResolver';
import { PresentationModel } from 'app/views/presentations/models/presentation.model';

@Injectable()
export class PresentationService {
    private readonly defaultLayout = 'FourSectionsLayout';
    private logger: Logger;

    constructor(
        loggerFactory: LoggerFactory,
        @Inject(PresentationHandler) private presentationHandlers: PresentationHandler[],
        private presentationRepository: PresentationRepository,
        private userRepository: UserRepository,
        private deviceRepository: DeviceRepository,
        private devicesGroupRepository: DevicesGroupRepository,
        private storageService: StorageService,
        private layoutResolver: LayoutResolver,
        private auth: AuthService,
        private clock: Clock
    ) {
        this.logger = loggerFactory.create('PresentationService');
        this.presentationHandlers = this.presentationHandlers.sort((x, y) => x.priority - y.priority);
    }

    public async getPresentation(id: string): Promise<PresentationModel> {
        if (!id) return undefined;

        return Object.assign(new PresentationModel(), await this.presentationRepository.get(id));
    }

    public async createPresentation() {
        let presentation = new PresentationModel(this.defaultLayout, '', '', {});
        presentation.creationDate = this.clock.now;
        presentation.theme = 'default';
        let user = await this.auth.getCurrentUser();
        presentation.owners[user.uid] = true;
        return presentation;
    }

    public async getThumbnailUrl(presentation: PresentationModel) {
        if (!presentation || !presentation.thumbnailPath) { return; }

        try {
            for (let i = 0; i < presentation.sections.length; i++) {
                const section = presentation.sections[i];
                if (section.files) {
                    for (const file of section.files) {
                        if (file.category === StorageCategories.Images || file.category === StorageCategories.Thumbnails) {
                            if (file.url) {
                                return file.url;
                            } else {
                                return this.storageService.getDownloadUrl(presentation.id, i.toString(), file.id);
                            }
                        }
                    }
                }
            }
            return undefined;
        } catch (err) {
            this.logger.error('Unable to retrieve thumbnail download url.', err);
        }
    }

    public watchThumbnail(presentationId: string, callback: (thumbnailPath) => void, onError: (error) => void) {
        let sub = this.presentationRepository.watch('value', presentationId)
            .subscribe(async val => {
                if (val && val.thumbnailPath) {
                    await callback(val.thumbnailPath);
                    sub.unsubscribe();
                }
            },
            error => onError(error)
            );
    }

    public async deletePresentation(id: string) {
        const presentation = await this.presentationRepository.get(id);
        await this.onDelete(presentation);
        await this.presentationRepository.remove(id);
        await this.removePresentationFromUserDevices(id);
        await this.removePresentationFromUserDeviceGroups(id);
        // Method call order is critical
        await this.removePresentationFromUser(id);
    }

    private async onSave(presentation: PresentationModel) {
        for (const handler of this.presentationHandlers) {
            await handler.onSave(presentation);
        }
    }

    private async onDelete(presentation: PresentationModel) {
        for (const handler of this.presentationHandlers) {
            await handler.onDelete(presentation);
        }
    }

    public async savePresentation(presentationModel: PresentationModel) {
        if (!presentationModel.name) { throw new Error('Cannot save a presentation without a name'); }
        if (!presentationModel.owners) { throw new Error('Cannot save a presentation without owners'); }
        if (presentationModel.id && !presentationModel.creationDate) { throw new Error('Cannot save ane existing presentation without a creation date.'); }

        presentationModel.modificationDate = this.clock.now;
        if (!presentationModel.id) {
            presentationModel.id = await this.presentationRepository.generateKey();
            presentationModel.creationDate = presentationModel.modificationDate;
        }

        if (!presentationModel.id) {
            throw new Error('Cannot save a presentation without an id');
        }

        await this.addPresentationToUser(presentationModel);

        try {
            await this.onSave(presentationModel);
            await this.presentationRepository.update(presentationModel.id, presentationModel);
        } catch (err) {
            this.logger.error('An error occured while setting up the presentation.', err);
            this.removePresentationFromUser(presentationModel.id);
            throw err;
        }
    }

    private async addPresentationToUser(presentationModel: PresentationModel) {
        let authUser = await this.auth.getCurrentUser();
        let userModel = await this.userRepository.get(authUser.uid);

        if (!userModel.presentations) {
            userModel.presentations = [];
        }

        const isAlreadyAssigned = userModel.presentations.indexOf(presentationModel.id) > -1;
        if (!isAlreadyAssigned) {
            userModel.presentations.push(presentationModel.id);
            await this.userRepository.update(authUser.uid, userModel);
        }
    }

    private async removePresentationFromUser(id: string) {
        let authUser = await this.auth.getCurrentUser();
        let userModel = await this.userRepository.get(authUser.uid);
        if (userModel.presentations) {
            userModel.presentations = userModel.presentations.filter(obj => obj !== id);
            await this.userRepository.set(authUser.uid, userModel);
        }
    }

    private async removePresentationFromUserDevices(id: string) {
        // Get user
        let authUser = await this.auth.getCurrentUser();
        let userModel = await this.userRepository.get(authUser.uid);

        if (userModel.presentations && userModel.devices) {
            // User has device(s) iterate through them
            for (let deviceId of userModel.devices) {
                try {
                    let deviceModel = await this.deviceRepository.get(deviceId);
                    await this.removePresentationFromSchedule(deviceModel.schedule, id);
                    await this.deviceRepository.update(deviceId, deviceModel);
                } catch (ex) {
                    this.logger.error('There was an error while removing presentation from device ' + deviceId, ex);
                }
            }
        }
    }

    private async removePresentationFromUserDeviceGroups(id: string) {
        // Get user
        let authUser = await this.auth.getCurrentUser();
        let userModel = await this.userRepository.get(authUser.uid);

        if (userModel.presentations && userModel.devicesGroups) {
            // User has device group(s) iterate through them
            for (let deviceGroupId of userModel.devicesGroups) {
                let deviceGroupModel = await this.devicesGroupRepository.get(deviceGroupId);

                await this.removePresentationFromSchedule(deviceGroupModel.schedule, id);
                await this.devicesGroupRepository.update(deviceGroupId, deviceGroupModel);
            }
        }
    }

    private async removePresentationFromSchedule(schedule: ScheduleModel, presId: string) {
        if (schedule === undefined) {
            return;
        }

        // Check match with default presentation
        if (schedule.defaultPresentationId === presId) { schedule.defaultPresentationId = undefined; }

        // Check match with schedule
        if (schedule.entries) {
            // Device has shedule entries, iterate through them
            for (let i = 0; i < schedule.entries.length; i++) {
                if (schedule.entries[i].presentationId !== presId) { continue; }
                schedule.entries[i] = undefined;
            }
        }
    }

    public async getUserPresentationList(userId: string) {
        let user = await this.userRepository.get(userId);
        let presentations: PresentationModel[] = [];
        if (user.presentations && user.presentations.length !== 0) {
            for (let presentationId of user.presentations) {
                try {
                    let presentation = await this.presentationRepository.get(presentationId);
                    presentations.push(presentation);
                } catch (err) {
                    this.logger.error(`An error occured while getting the user '${userId}' presentation '${presentationId}'.`);
                }
            }

            presentations.sort((a: PresentationModel, b: PresentationModel) => {
                return b.modificationDate - a.modificationDate;
            });
        }
        return presentations;
    }

    public getOverlapingScheduleEntriesList(entries: ScheduleEntryModel[]): ScheduleEntryModel[] {
        let overlapings: ScheduleEntryModel[] = [];
        for (let entryA of entries) {
            for (let entryB of entries) {
                if (entryA === entryB) { continue; }
                if (this.isOverlapping(entryA, entryB)) {
                    overlapings.push(entryA);
                }
            }
        }
        return overlapings;
    }

    public scheduleOverlap(schedule: ScheduleModel): boolean {
        for (let entryA of schedule.entries) {
            for (let entryB of schedule.entries) {
                if (entryA !== entryB && this.isOverlapping(entryA, entryB)) {
                    return true;
                }
            }
        }
        return false;
    }

    public isOverlapping(entryA: ScheduleEntryModel, entryB: ScheduleEntryModel): boolean {
        return ((entryA.startDate < entryB.endDate && entryA.endDate > entryB.startDate))
            || (entryA.startDate === entryB.startDate && entryA.endDate === entryB.endDate);
    }
}



// WEBPACK FOOTER //
// ./src/app/services/presentation/presentation.service.ts